import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { goodsReturnsFromCustomer } from '@/api/goodsReturns';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import { taxInvoicesOutgoing } from '@/api/taxInvoices';
import Drawer from '@/components/Drawer';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';

const fmt = (v: string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const today = () => new Date().toISOString().slice(0, 10);

export default function GoodsReturnFromCustomerViewer() {
  const confirm = useConfirm();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();

  const [rkOpen, setRkOpen] = useState(false);
  const [rkInvoiceId, setRkInvoiceId] = useState('');
  const [rkNumber, setRkNumber] = useState('');
  const [rkDate, setRkDate] = useState(today());
  const [rkReason, setRkReason] = useState('103');

  const docQ = useQuery({
    queryKey: ['grfc', id],
    queryFn: () => goodsReturnsFromCustomer.get(id!),
    enabled: !!id,
  });
  const post = useMutation({
    mutationFn: () => goodsReturnsFromCustomer.post(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['grfc', id] });
      qc.invalidateQueries({ queryKey: ['grfc-list'] });
    },
  });
  const unpost = useMutation({
    mutationFn: () => goodsReturnsFromCustomer.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['grfc', id] });
      qc.invalidateQueries({ queryKey: ['grfc-list'] });
    },
  });
  const remove = useMutation({
    mutationFn: () => goodsReturnsFromCustomer.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['grfc-list'] });
      navigate('/goods-returns-from-customer');
    },
  });

  // Список исходных НН исходящих, из которых можно создать РК.
  // Фильтруем по контрагенту возврата + только проведённые.
  const invoicesQ = useQuery({
    queryKey: ['outgoing-invoices-for-rk', docQ.data?.contractor_id],
    queryFn: () => taxInvoicesOutgoing.list({
      contractor_id: docQ.data?.contractor_id,
      posted: true,
      limit: 200,
    }),
    enabled: rkOpen && !!docQ.data?.contractor_id,
  });

  const createRk = useMutation({
    mutationFn: () => taxInvoicesOutgoing.createCorrectionFromReturn({
      organization_id: docQ.data!.organization_id,
      return_id: id!,
      corrected_invoice_id: rkInvoiceId,
      invoice_number: rkNumber,
      invoice_date: rkDate,
      correction_reason: rkReason || null,
    }),
    onSuccess: (rk) => {
      setRkOpen(false);
      navigate(`/tax-invoices-outgoing/${rk.id}`);
    },
  });

  const d = docQ.data;
  if (!d) return <div className="card">Завантаження...</div>;

  const isPosted = d.posted;

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          Повернення від покупця №{d.document_number}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ {t('mo.posted_yes')}
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <DocumentToolbar
            onBackToList={() => navigate('/goods-returns-from-customer')}
            onSave={() => {}}
            saveDisabled
            isPosted={isPosted}
            onPost={() => post.mutate()}
            postPending={post.isPending}
            onUnpost={() => unpost.mutate()}
            unpostPending={unpost.isPending}
            onDelete={() => { confirm(t('common.confirm_delete')).then((ok) => { if (ok) remove.mutate(); }); }}
            deletePending={remove.isPending}
          />
          {isPosted && (
            <button onClick={() => setRkOpen(true)}>+ Створити РК</button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
        <div className="field"><label>Дата</label><input value={new Date(d.document_date).toLocaleString('uk-UA')} readOnly /></div>
        <div className="field"><label>Сума без ПДВ</label><input value={fmt(d.amount_without_vat)} readOnly style={{ textAlign: 'right' }} /></div>
        <div className="field"><label>Усього з ПДВ</label><input value={fmt(d.total_amount)} readOnly style={{ textAlign: 'right', fontWeight: 600 }} /></div>
      </div>
      <div className="field">
        <label>Документ-підстава</label>
        <input value={d.base_sale_id ? `doc.goods_sales / ${d.base_sale_id}` : '—'} readOnly />
      </div>
      {d.comment && (
        <div className="field"><label>Коментар</label><input value={d.comment} readOnly /></div>
      )}

      <h3 style={{ marginTop: '1.5rem' }}>Рядки</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem', width: 40 }}>#</th>
            <th style={{ padding: '0.5rem', width: 100, textAlign: 'right' }}>Кількість</th>
            <th style={{ padding: '0.5rem', width: 110, textAlign: 'right' }}>Ціна</th>
            <th style={{ padding: '0.5rem', width: 130, textAlign: 'right' }}>Сума без ПДВ</th>
            <th style={{ padding: '0.5rem', width: 110, textAlign: 'right' }}>ПДВ</th>
            <th style={{ padding: '0.5rem', width: 130, textAlign: 'right' }}>Усього</th>
            <th style={{ padding: '0.5rem', width: 130, textAlign: 'right' }}>Собівартість</th>
          </tr>
        </thead>
        <tbody>
          {d.items.map((it) => (
            <tr key={it.id} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '0.5rem' }}>{it.line_number}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{Number(it.quantity).toLocaleString('uk-UA')}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(it.price)}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(it.amount_without_vat)}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(it.vat_amount)}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>{fmt(it.total_amount)}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                {Number(it.cost_amount) > 0 ? fmt(it.cost_amount) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Drawer
        open={rkOpen}
        title="Розрахунок корегування до НН"
        onClose={() => setRkOpen(false)}
        footer={
          <>
            <button onClick={() => setRkOpen(false)}>{t('common.cancel')}</button>
            <button
              className="primary"
              disabled={!rkInvoiceId || !rkNumber || createRk.isPending}
              onClick={() => createRk.mutate()}
            >
              Створити РК
            </button>
          </>
        }
      >
        <p style={{ color: '#64748b', fontSize: '0.9rem' }}>
          РК створюється на основі цього повернення і коригує одну з раніше
          виписаних податкових накладних. Сума РК буде дорівнювати сумі
          повернення ({fmt(d.total_amount)} грн).
        </p>
        <div className="field">
          <label>Виправлювана НН</label>
          <select value={rkInvoiceId} onChange={(e) => setRkInvoiceId(e.target.value)}>
            <option value="">—</option>
            {invoicesQ.data?.items.filter((i) => i.posted).map((i) => (
              <option key={i.id} value={i.id}>
                №{i.invoice_number} від {new Date(i.invoice_date).toLocaleDateString('uk-UA')} — {fmt(i.total_amount)}
              </option>
            ))}
          </select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
          <div className="field">
            <label>№ РК</label>
            <input value={rkNumber} onChange={(e) => setRkNumber(e.target.value)} />
          </div>
          <div className="field">
            <label>Дата РК</label>
            <DatePopover value={rkDate} onChange={(v) => setRkDate(v)} />
          </div>
        </div>
        <div className="field">
          <label>Причина коригування (код)</label>
          <input
            value={rkReason}
            onChange={(e) => setRkReason(e.target.value)}
            placeholder="Напр. 103 — повернення товарів"
          />
        </div>
      </Drawer>
    </div>
  );
}
